import { DealPageHeader } from "@/components/deal-page-header";
import { DealNav } from "@/components/deal-nav";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect } from "react";
import { useServerFn } from "@tanstack/react-start";
import { useQuery } from "@tanstack/react-query";
import { getDealDetail } from "@/lib/deals.functions";
import { getTermSheetReportForDeal } from "@/lib/term-sheet.functions";
import { useAuth } from "@/hooks/use-auth";

export const Route = createFileRoute(
  "/_authed/deals/$dealId_/term-sheet/$reportId",
)({
  head: () => ({
    meta: [
      { title: "Indicative Term Sheet: CapitalDart" },
      { name: "robots", content: "noindex,nofollow" },
    ],
  }),
  component: TermSheetReportPage,
});

/* Defensive readers: report_json is model assembled. */
type Dict = Record<string, unknown>;

function asDict(v: unknown): Dict | null {
  return v && typeof v === "object" && !Array.isArray(v) ? (v as Dict) : null;
}
function asDicts(v: unknown): Dict[] {
  return (Array.isArray(v) ? v : []).map(asDict).filter((d): d is Dict => !!d);
}
function asStr(v: unknown): string | null {
  return typeof v === "string" && v.trim() ? v.trim() : null;
}
function asStrs(v: unknown): string[] {
  return (Array.isArray(v) ? v : [])
    .map(asStr)
    .filter((s): s is string => !!s);
}

function SectionLabel({ children }: { children: string }) {
  return (
    <h2 className="ts-label mt-8 text-[12px] font-semibold uppercase tracking-[0.14em] text-brand-muted">
      {children}
    </h2>
  );
}

function Paragraphs({ items }: { items: string[] }) {
  return (
    <>
      {items.map((p, i) => (
        <p key={i} className="ts-para mt-2 text-[15px] leading-relaxed text-brand-ink">
          {p}
        </p>
      ))}
    </>
  );
}

function TermSheetReportPage() {
  const { dealId, reportId } = Route.useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const fetchReport = useServerFn(getTermSheetReportForDeal);
  const fetchDeal = useServerFn(getDealDetail);

  const reportQuery = useQuery({
    queryKey: ["term-sheet-report", dealId, reportId],
    queryFn: () => fetchReport({ data: { dealId, reportId } }),
    enabled: !!user,
  });

  const dealQuery = useQuery({
    queryKey: ["deal-detail", dealId],
    queryFn: () => fetchDeal({ data: { dealId } }),
    enabled: !!user,
  });

  const row = (reportQuery.data?.report ?? null) as Dict | null;
  const status = asStr(row?.["report_status"]);

  useEffect(() => {
    if (status === "processing") {
      navigate({ to: "/deals/$dealId", params: { dealId } });
    }
  }, [status, dealId, navigate]);

  useEffect(() => {
    document.body.classList.add("model-review-print");
    return () => {
      document.body.classList.remove("model-review-print");
    };
  }, []);

  if (reportQuery.isLoading || dealQuery.isLoading) {
    return (
      <div className="mx-auto max-w-4xl px-4 py-10 text-sm text-muted-foreground">
        Loading the report...
      </div>
    );
  }

  if (reportQuery.isError || !row) {
    return (
      <div className="mx-auto max-w-4xl space-y-3 px-4 py-10">
        <p className="text-sm text-brand-ink">This report is not available.</p>
        <Link
          to="/deals/$dealId"
          params={{ dealId }}
          className="text-sm underline underline-offset-2"
        >
          Back to the deal
        </Link>
      </div>
    );
  }

  const dealName =
    asStr(
      (dealQuery.data as { deal?: { name?: string } } | undefined)?.deal?.name,
    ) ?? "Deal";
  const pageTitle = `${dealName} Indicative Term Sheet`;

  const handleDownloadPdf = () => {
    const originalTitle = document.title;
    document.title = pageTitle;
    const restore = () => {
      document.title = originalTitle;
      window.removeEventListener("afterprint", restore);
    };
    window.addEventListener("afterprint", restore);
    setTimeout(() => {
      if (document.title === pageTitle) {
        document.title = originalTitle;
        window.removeEventListener("afterprint", restore);
      }
    }, 1000);
    window.print();
  };

  if (status === "failed") {
    return (
      <div className="mx-auto max-w-4xl space-y-3 px-4 py-10">
        <h1 className="font-serif text-2xl text-brand-ink">
          <span className="block">{dealName}</span>
          <span className="block">Indicative Term Sheet</span>
        </h1>
        <p className="text-sm text-brand-ink">
          This report did not finish. Any credits charged were returned.
        </p>
        {asStr(row["failed_reason"]) && (
          <p className="text-xs text-muted-foreground">
            Reason: {asStr(row["failed_reason"])}
          </p>
        )}
        <Link
          to="/deals/$dealId"
          params={{ dealId }}
          className="text-sm underline underline-offset-2"
        >
          Back to the deal
        </Link>
      </div>
    );
  }

  if (status === "processing") return null;

  const report = asDict(row["report_json"]) ?? {};
  const doc = asDict(report["document"]) ?? {};
  const titleBlock = asDict(doc["title_block"]) ?? {};
  const confidentiality = asStr(doc["confidentiality_line"]);
  const disclaimer = asStr(report["disclaimer"]);

  const companyName = asStr(titleBlock["company"]) ?? dealName;
  const transactionTitle = asStr(titleBlock["transaction_title"]);
  const subtitle = asStr(titleBlock["subtitle"]);
  const overview = asStrs(doc["overview"]);
  const summaryTerms = asDicts(doc["summary_table"]).filter(
    (r) => asStr(r["label"]) || asStr(r["value"]),
  );
  const highlights = asStrs(doc["investment_highlights"]);
  const processText = asStr(doc["process"]);
  const processParas = processText
    ? processText.split(/\n{2,}|\n/).map((x) => x.trim()).filter(Boolean)
    : [];

  const empty =
    overview.length === 0 &&
    summaryTerms.length === 0 &&
    highlights.length === 0 &&
    processParas.length === 0;

  return (
    <div className="term-sheet-page mx-auto max-w-4xl px-4 py-8">
      <style>{`
        @media print {
          @page { margin: 0.8in; }
          body.model-review-print {
            background: #fff !important;
            color: #000 !important;
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
          }
          body.model-review-print header.sticky { display: none !important; }
          .term-sheet-page .no-print { display: none !important; }
          .term-sheet-page {
            max-width: none !important;
            padding: 0 !important;
            margin: 0 !important;
          }
          .term-sheet-doc {
            border: none !important;
            box-shadow: none !important;
            border-radius: 0 !important;
            background: #fff !important;
            padding: 0 !important;
            margin: 0 !important;
            max-width: none !important;
          }
          .term-sheet-doc h1,
          .term-sheet-doc h2,
          .term-sheet-doc h3 { color: #000 !important; break-after: avoid; }
          .term-sheet-doc p,
          .term-sheet-doc li,
          .term-sheet-doc td,
          .term-sheet-doc th { color: #000 !important; }
          .term-sheet-doc .text-muted-foreground,
          .term-sheet-doc .text-brand-muted { color: #333 !important; }
          .term-sheet-doc .ts-label { break-after: avoid; page-break-after: avoid; }
          .term-sheet-doc table {
            width: 100% !important;
            border-collapse: collapse !important;
          }
          .term-sheet-doc tr,
          .term-sheet-doc li,
          .term-sheet-doc .ts-para {
            break-inside: avoid;
            page-break-inside: avoid;
          }
          .term-sheet-doc td, .term-sheet-doc th {
            padding: 0.3rem 0.5rem 0.3rem 0 !important;
            vertical-align: top !important;
            border: none !important;
            border-bottom: 1px solid #ddd !important;
          }
          .term-sheet-doc .border-brand-line { border-color: #ddd !important; }
        }
      `}</style>

      <div className="no-print space-y-6">
        <DealPageHeader dealId={dealId} />
        <DealNav dealId={dealId} active="reports" />

        <div className="flex items-start justify-between gap-4">
          <h1 className="font-serif text-2xl text-brand-ink">
            <span className="block">{dealName}</span>
            <span className="block">Indicative Term Sheet</span>
          </h1>
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={handleDownloadPdf}
              className="shrink-0 text-sm underline underline-offset-2"
            >
              Download PDF
            </button>
            <Link
              to="/deals/$dealId/reports"
              params={{ dealId }}
              search={{ product: "term_sheet" as const }}
              className="shrink-0 text-sm underline underline-offset-2"
            >
              Back to reports
            </Link>
          </div>
        </div>
      </div>

      <article className="term-sheet-doc mt-6 rounded-lg border border-brand-line bg-white px-10 py-12 sm:px-16 sm:py-14">
        {/* Title block */}
        <header>
          <h1 className="font-serif text-[30px] leading-tight text-brand-ink">
            {companyName}
          </h1>
          {transactionTitle && (
            <p className="mt-1 font-serif text-[17px] text-brand-ink/80">
              {transactionTitle}
            </p>
          )}
          {subtitle && (
            <p className="mt-1 text-[14px] text-brand-muted">{subtitle}</p>
          )}
          {confidentiality && (
            <p className="mt-3 text-[12px] text-brand-muted">{confidentiality}</p>
          )}
        </header>

        {empty && (
          <p className="mt-8 text-sm text-brand-muted">
            This report has no content to display.
          </p>
        )}

        {overview.length > 0 && (
          <>
            <SectionLabel>Overview</SectionLabel>
            <Paragraphs items={overview} />
          </>
        )}

        {summaryTerms.length > 0 && (
          <>
            <SectionLabel>Summary of Indicative Terms</SectionLabel>
            <table className="mt-3 w-full text-left text-[15px]">
              <tbody>
                {summaryTerms.map((r, i) => (
                  <tr
                    key={i}
                    className="border-b border-brand-line/70 align-top"
                  >
                    <td className="w-[30%] py-2 pr-4 font-semibold text-brand-ink">
                      {asStr(r["label"]) ?? ""}
                    </td>
                    <td className="py-2 text-brand-ink">
                      {asStr(r["value"]) ?? ""}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </>
        )}

        {highlights.length > 0 && (
          <>
            <SectionLabel>Investment Highlights</SectionLabel>
            <ul className="mt-3 list-disc space-y-1.5 pl-5 text-[15px] leading-relaxed text-brand-ink">
              {highlights.map((h, i) => (
                <li key={i}>{h}</li>
              ))}
            </ul>
          </>
        )}

        {processParas.length > 0 && (
          <>
            <SectionLabel>Indicative Process</SectionLabel>
            <Paragraphs items={processParas} />
          </>
        )}

        {disclaimer && (
          <p className="ts-para mt-10 border-t border-brand-line/70 pt-4 text-[12px] leading-relaxed text-brand-muted">
            {disclaimer}
          </p>
        )}
      </article>
    </div>
  );
}
